function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { CssContainer } from "../../components/container";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import { Button } from "@progress/kendo-react-buttons";
import { PageHeader, Ribbon, Card, Pill, Empty, Search, Filters, PlunkDropDown } from "../../components/analytics-kit";
import { hasTenantFeature } from "../../functions/tenant-features";
import { INPUT } from "../../components/form-styles";
import { api, qs, errMsg, toast, idStr, num, pct, money, railLabel, kindLabel, statusLabel, statusTone, recipientTone, dateTime, dateOnly, GUARD, refreshGuard, guardState, canArchive, canDelete, permanentDelete, archivedFrom } from "./api";
import Funnel from "./funnel";

/*
 * Campaign detail (BULK_EMAIL_MANAGEMENT §10.3, mockup 4). While Sending the
 * page polls GET campaigns/{id} every 15 s for recipientCounts and the guard
 * rates; the resolved funnel (resolvedJson) is the truth, the wizard estimate
 * is shown only until the dispatcher has resolved. Rail campaigns get "Open in
 * Mailchimp / Constant Contact" when the provider url is known.
 * Header actions (2026-10-05): Archive (PUT {id}/status Archived), Restore
 * (back to archived.from), Delete (Draft / Canceled inline Yes / Keep;
 * Sent / Failed / Archived only after typing the campaign name — permanent,
 * stats included).
 */

const POLL_MS = 15000;
const PAGE = 100;
const RECIPIENT_STATUSES = ["Queued", "Sent", "Delivered", "Bounced", "Dropped", "Complained", "Unsubscribed", "Failed"];
class EmailCampaignDetailViewComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "load", async () => {
      try {
        const d = await api("GET", `/campaigns/${this.id()}`);
        if (!this._alive) return;
        this.setState({
          d,
          error: null
        });
        this.schedulePoll(d);
        if (d && d.campaign && d.campaign.status === "Sent" && this.state.compare === undefined && !this._compareT) this.loadCompare();
      } catch (e) {
        if (this._alive) this.setState({
          error: errMsg(e, "This campaign could not be loaded.")
        });
      }
    });
    /** GET campaigns/{id}/compare (2026-10-04): the previous Sent campaign's stats next to this one. Hidden when the route is absent (404/405/501) or the campaign has not been sent. */
    _defineProperty(this, "loadCompare", async () => {
      this._compareT = true;
      try {
        const r = await api("GET", `/campaigns/${this.id()}/compare`);
        if (this._alive) this.setState({
          compare: r && typeof r === "object" ? r : null
        });
      } catch (e) {
        if (this._alive) this.setState({
          compare: null
        });
      }
    });
    _defineProperty(this, "loadLinks", () => api("GET", `/campaigns/${this.id()}/links`).then(d => this._alive && this.setState({
      links: d && d.items || []
    })).catch(() => this._alive && this.setState({
      links: []
    })));
    _defineProperty(this, "loadTimeline", () => api("GET", `/campaigns/${this.id()}/timeline`).then(d => this._alive && this.setState({
      timeline: d && d.items || []
    })).catch(() => this._alive && this.setState({
      timeline: []
    })));
    _defineProperty(this, "loadRecipients", async (skip, quiet) => {
      const {
        recStatus,
        recQ
      } = this.state;
      try {
        const d = await api("GET", `/campaigns/${this.id()}/recipients${qs({
          status: recStatus,
          q: recQ.trim(),
          $top: PAGE,
          $skip: skip
        })}`);
        if (!this._alive) return;
        const items = (d && d.items || []).map(r => ({
          ...r,
          id: idStr(r.id),
          personId: idStr(r.personId)
        }));
        this.setState({
          recipients: skip && !quiet ? (this.state.recipients || []).concat(items) : items,
          recTotal: d && d.totalCount || 0,
          recSkip: skip
        });
      } catch (e) {
        if (this._alive && !quiet) this.setState({
          recipients: [],
          recTotal: 0
        });
      }
    });
    _defineProperty(this, "recFilter", k => v => this.setState({
      [k]: v
    }, () => this.loadRecipients(0)));
    _defineProperty(this, "act", async (action, body, successMsg) => {
      this.setState({
        busy: action,
        actionErr: null,
        confirmCancel: false
      });
      try {
        const d = await api("POST", `/campaigns/${this.id()}/${action}`, body);
        if (!this._alive) return null;
        if (successMsg) toast(successMsg);
        this.setState({
          busy: null
        });
        await this.load();
        this.loadTimeline();
        return d;
      } catch (e) {
        if (this._alive) this.setState({
          busy: null,
          actionErr: errMsg(e, "The action failed.")
        });
        return null;
      }
    });
    /** PUT campaigns/{id}/status: "Archived" to archive, or the status it was archived from to restore. */
    _defineProperty(this, "setStatus", async (status, msg) => {
      this.setState({
        busy: "status",
        actionErr: null,
        confirmCancel: false,
        confirmDelete: null
      });
      try {
        const r = await api("PUT", `/campaigns/${this.id()}/status`, {
          status
        });
        if (!this._alive) return;
        toast(r && r.message || msg);
        this.setState({
          busy: null
        });
        await this.load();
        this.loadTimeline();
      } catch (e) {
        if (this._alive) this.setState({
          busy: null,
          actionErr: errMsg(e, "The status could not be changed.")
        });
      }
    });
    /** DELETE campaigns/{id}[?confirm=name]; a 409 confirmRequired (a draft with sent children) upgrades the confirm to the typed-name one. */
    _defineProperty(this, "remove", async named => {
      this.setState({
        busy: "delete",
        actionErr: null
      });
      try {
        const r = await api("DELETE", `/campaigns/${this.id()}${named ? `?confirm=${encodeURIComponent(this.state.typed.trim())}` : ""}`);
        if (!this._alive) return;
        toast(r && r.permanent ? `Deleted permanently: ${num(r.recipients)} recipients and ${num(r.events)} event rows removed.` : "Deleted.");
        this.props.history.push("/Email/campaigns");
      } catch (e) {
        if (!this._alive) return;
        if (e.code === "confirmRequired") {
          this.setState({
            busy: null,
            confirmDelete: "named",
            typed: ""
          });
          return;
        }
        this.setState({
          busy: null,
          confirmDelete: null,
          actionErr: errMsg(e, "The campaign could not be deleted.")
        });
      }
    });
    _defineProperty(this, "duplicate", async () => {
      const d = await this.act("duplicate", this.state.dupName ? {
        name: this.state.dupName
      } : {}, "Duplicated as a new Draft.");
      const id = d && d.campaign && idStr(d.campaign.id);
      if (id) this.props.history.push(`/Email/campaigns/edit/${id}`);
    });
    _defineProperty(this, "createResend", async () => {
      const r = this.state.resend;
      if (!r || !(r.subject || "").trim()) {
        this.setState({
          actionErr: "A subject for the resend is required."
        });
        return;
      }
      const d = await this.act("resend", {
        afterDays: r.afterDays || 3,
        subject: r.subject.trim()
      }, "Resend campaign created as a Draft.");
      const id = d && d.campaign && idStr(d.campaign.id);
      if (id) this.props.history.push(`/Email/campaigns/detail/${id}`);
    });
    this.state = {
      d: null,
      links: null,
      timeline: null,
      recipients: null,
      recTotal: 0,
      recSkip: 0,
      recStatus: "all",
      recQ: "",
      error: null,
      busy: null,
      actionErr: null,
      confirmCancel: false,
      resend: null,
      dupName: null,
      compare: undefined,
      confirmDelete: null,
      typed: ""
    };
    this._alive = true;
  }
  id() {
    return this.props.params.id;
  }
  async componentDidMount() {
    refreshGuard().then(() => {
      if (this._alive !== false) this.forceUpdate();
    });
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    await this.load();
    this.loadLinks();
    this.loadTimeline();
    this.loadRecipients(0);
  }
  componentDidUpdate(prev) {
    if (prev.params.id !== this.props.params.id) {
      this.setState({
        d: null,
        links: null,
        timeline: null,
        recipients: null,
        compare: undefined
      }, () => {
        this.load();
        this.loadLinks();
        this.loadTimeline();
        this.loadRecipients(0);
      });
    }
  }
  componentWillUnmount() {
    this._alive = false;
    clearTimeout(this._pollT);
  }
  schedulePoll(d) {
    clearTimeout(this._pollT);
    const st = d && d.campaign && d.campaign.status;
    if (st === "Sending" || st === "Scheduled") this._pollT = setTimeout(() => {
      this.load();
      if (st === "Sending" && !this.state.recSkip) this.loadRecipients(0, true);
    }, POLL_MS);
  }
  renderDeleteConfirm(c) {
    const {
      busy,
      confirmDelete,
      typed
    } = this.state;
    if (confirmDelete === "named") {
      const ok = typed.trim().toLowerCase() === String(c.name || "").trim().toLowerCase();
      return /*#__PURE__*/React.createElement("span", {
        className: "ec-confirm"
      }, /*#__PURE__*/React.createElement("input", {
        className: "ec-input",
        value: typed,
        placeholder: `Type “${c.name}” to delete`,
        "aria-label": "Campaign name",
        onChange: e => this.setState({
          typed: e.target.value
        })
      }), /*#__PURE__*/React.createElement(Button, {
        disabled: !!busy || !ok,
        style: {
          color: "var(--plk-danger-text)"
        },
        onClick: () => this.remove(true)
      }, busy === "delete" ? "…" : "Delete"), /*#__PURE__*/React.createElement(Button, {
        disabled: !!busy,
        onClick: () => this.setState({
          confirmDelete: null,
          typed: ""
        })
      }, "Keep"), /*#__PURE__*/React.createElement("span", {
        className: "ec-help"
      }, "Permanent: removes its stats, recipients and events from every report."));
    }
    return /*#__PURE__*/React.createElement("span", {
      className: "ec-confirm"
    }, /*#__PURE__*/React.createElement("span", null, c.status === "Draft" ? "Delete this draft?" : "Delete this canceled campaign?"), /*#__PURE__*/React.createElement(Button, {
      disabled: !!busy,
      style: {
        color: "var(--plk-danger-text)"
      },
      onClick: () => this.remove(false)
    }, busy === "delete" ? "…" : "Yes, delete"), /*#__PURE__*/React.createElement(Button, {
      disabled: !!busy,
      onClick: () => this.setState({
        confirmDelete: null
      })
    }, "Keep"));
  }
  /** "Compared with <previous> (<date>)": open, click, conversions, revenue, unsubscribe with deltas (points for rates, % for counts). */
  renderCompare() {
    const {
      compare,
      d
    } = this.state;
    const st = d && d.campaign && d.campaign.status;
    if (compare === undefined || compare === null || st !== "Sent") return null;
    const prev = compare.previous || null;
    if (!prev) return /*#__PURE__*/React.createElement(Card, {
      span: 12,
      title: "Compared with the last campaign"
    }, /*#__PURE__*/React.createElement(Empty, null, "No earlier campaign to compare."));
    const cur = compare.current || {};
    const old = compare.previousStats || {};
    const dl = compare.deltas || {};
    const ppDelta = k => dl[k] != null ? Number(dl[k]) : cur[k.replace(/Pp$/, "")] != null && old[k.replace(/Pp$/, "")] != null ? Number(cur[k.replace(/Pp$/, "")]) - Number(old[k.replace(/Pp$/, "")]) : null;
    const pctDelta = k => dl[k] != null && old[k] ? Number(dl[k]) / Number(old[k]) * 100 : old[k] ? (Number(cur[k]) - Number(old[k])) / Number(old[k]) * 100 : null;
    const rows = [{
      l: "Open rate",
      v: pct(cur.openRate),
      p: pct(old.openRate),
      d: ppDelta("openRatePp"),
      unit: "pp"
    }, {
      l: "Click rate",
      v: pct(cur.clickRate),
      p: pct(old.clickRate),
      d: ppDelta("clickRatePp"),
      unit: "pp"
    }, {
      l: "Conversions",
      v: num(cur.conversions),
      p: num(old.conversions),
      d: pctDelta("conversions"),
      unit: "%"
    }, {
      l: "Revenue",
      v: money(cur.revenue),
      p: money(old.revenue),
      d: pctDelta("revenue"),
      unit: "%"
    }, {
      l: "Unsubscribe rate",
      v: pct(cur.unsubscribeRate, 2),
      p: pct(old.unsubscribeRate, 2),
      d: ppDelta("unsubscribeRatePp"),
      unit: "pp",
      invert: true
    }];
    const arrow = r => {
      if (r.d == null || !isFinite(r.d)) return /*#__PURE__*/React.createElement(Pill, {
        tone: "neutral"
      }, "\u2014");
      const dir = r.d > 0.0005 ? "up" : r.d < -0.0005 ? "down" : "flat";
      const good = dir === "flat" ? null : dir === "up" !== !!r.invert;
      const tone = dir === "flat" ? "neutral" : good ? "success" : "danger";
      const txt = `${dir === "up" ? "▲" : dir === "down" ? "▼" : "•"} ${Math.abs(r.d).toFixed(r.unit === "pp" ? 1 : 0)} ${r.unit === "pp" ? "pts" : "%"}`;
      return /*#__PURE__*/React.createElement(Pill, {
        tone: tone
      }, txt);
    };
    return /*#__PURE__*/React.createElement(Card, {
      span: 12,
      title: /*#__PURE__*/React.createElement(React.Fragment, null, "Compared with ", prev.name || "the previous campaign", prev.sentAt ? ` (${dateOnly(prev.sentAt)})` : ""),
      hint: prev.sameLists ? "same lists" : "different lists; read the rates, not the counts",
      right: prev.id ? /*#__PURE__*/React.createElement(Link, {
        to: `/Email/campaigns/detail/${idStr(prev.id)}`,
        className: "dim",
        style: {
          fontSize: 12
        }
      }, "open it") : null
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-compare"
    }, rows.map(r => /*#__PURE__*/React.createElement("div", {
      key: r.l,
      className: "ec-compare-cell"
    }, /*#__PURE__*/React.createElement("div", {
      className: "k"
    }, r.l), /*#__PURE__*/React.createElement("div", {
      className: "v"
    }, r.v), /*#__PURE__*/React.createElement("div", {
      className: "p"
    }, arrow(r), " ", /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "was ", r.p))))), num(cur.sent) !== num(old.sent) ? /*#__PURE__*/React.createElement("div", {
      className: "mini",
      style: {
        marginTop: 6
      }
    }, num(cur.sent), " sent vs ", num(old.sent), " before \xB7 ", num(cur.delivered), " vs ", num(old.delivered), " delivered", cur.complaintRate != null ? ` · complaints ${pct(cur.complaintRate, 2)} vs ${pct(old.complaintRate, 2)}` : "") : null);
  }
  render() {
    if (!hasTenantFeature("emailAutomation")) return /*#__PURE__*/React.createElement(CssContainer, {
      cls: "margin-squeeze"
    }, /*#__PURE__*/React.createElement("p", {
      className: "module-empty"
    }, "Email isn't configured for this workspace."));
    const {
      d,
      error,
      links,
      timeline,
      recipients,
      recTotal,
      busy,
      actionErr,
      confirmCancel,
      resend,
      confirmDelete
    } = this.state;
    if (error && !d) return /*#__PURE__*/React.createElement(Error, {
      message: error
    });
    if (!d) return /*#__PURE__*/React.createElement(Loader, null);
    const c = d.campaign || {};
    const st = c.status;
    const s = d.stats || {};
    const counts = d.recipientCounts || {};
    const sch = d.schedule || {};
    const settings = d.settings || {};
    const next = d.nextTargets || [];
    const can = t => next.indexOf(t) >= 0;
    const editable = ["Draft", "Rejected", "Approved"].indexOf(st) >= 0;
    const sent = Number(s.sent) || 0;
    const hardRate = sent ? (Number(s.bouncedHard) || 0) / sent * 100 : 0;
    const complaintRate = Number(s.complaintRate) || 0;
    const total = Number(counts.total) || 0;
    const progressed = total ? total - (Number(counts.queued) || 0) : 0;
    const progressPct = total ? Math.round(progressed / total * 100) : 0;
    const variants = (s.variants || []).slice();
    const winner = d.variants && d.variants.winnerKey;
    const resolved = d.resolved || null;
    const audience = d.audience || {};
    const delivery = d.delivery && typeof d.delivery === "object" ? d.delivery : null;
    const localSend = delivery && String(delivery.mode || "").toLowerCase() === "local";
    const localSent = localSend ? Number(delivery.enqueued) || 0 : 0;
    const localTotal = localSend ? localSent + (Number(delivery.remaining) || 0) : 0;
    const resendPlannedAt = d.resendPlannedAt || c.resendPlannedAt || null;
    const subtitleBits = [kindLabel(c.kind), `${railLabel(c.rail)} rail`];
    if (c.containerId) subtitleBits.push("from Content Hub");
    if (c.reviewedBy && c.reviewStatus === "Approved") subtitleBits.push(`approved by ${c.reviewedBy} ${dateTime(c.reviewedDate)}`);
    if (c.sendAt) subtitleBits.push(`${st === "Sent" ? "sent" : "scheduled"} ${dateTime(c.sendAt, sch.timezone)}${sch.timezone ? ` ${sch.timezone}` : ""}`);
    if (d.sender) subtitleBits.push(`from ${d.sender.fromName} <${d.sender.fromEmail}>`);
    if (localSend && delivery.localHour != null) subtitleBits.push(`${delivery.localHour}:00 recipient-local`);
    const tiles = [{
      l: "Sent",
      v: num(s.sent),
      s: total ? `${num(counts.delivered)} delivered · ${num(counts.failed)} failed` : ""
    }, {
      l: "Delivered",
      v: pct(s.deliveryRate),
      s: `bounce ${pct(s.bounceRate)}`
    }, {
      l: "Clicks",
      v: num(s.uniqueClicks),
      s: `${pct(s.clickRate)} click rate`
    }, {
      l: "Opens (incl. machine)",
      v: pct(s.openRate),
      s: `CTOR ${pct(s.ctor)}`
    }, {
      l: "Unsubscribed",
      v: num(s.unsubscribes),
      s: pct(s.unsubscribeRate, 2)
    }, {
      l: "Conversions",
      v: num(s.conversions),
      s: money(s.revenue)
    }];
    const restoreTo = archivedFrom(c, d.archived);
    const actions = /*#__PURE__*/React.createElement("span", {
      className: "wa-actions"
    }, editable ? /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.props.history.push(`/Email/campaigns/edit/${idStr(c.id)}`)
    }, st === "Approved" ? "Edit / schedule" : "Edit") : null, st === "Sending" && can("Paused") ? /*#__PURE__*/React.createElement(Button, {
      disabled: !!busy,
      onClick: () => this.act("pause", {}, "Paused.")
    }, busy === "pause" ? "…" : "Pause") : null, st === "Paused" && can("Sending") ? /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !!busy,
      onClick: () => this.act("resume", {}, "Resuming: the guard re-checks first.")
    }, busy === "resume" ? "…" : "Resume") : null, ["Draft", "Scheduled", "Sending", "Paused"].indexOf(st) >= 0 ? confirmCancel ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 13,
        alignSelf: "center"
      }
    }, st === "Scheduled" ? "Unschedule and cancel?" : st === "Sending" ? "Stop sending and cancel?" : "Cancel this campaign?"), /*#__PURE__*/React.createElement(Button, {
      disabled: !!busy,
      onClick: () => this.setState({
        confirmCancel: false
      })
    }, "Keep"), /*#__PURE__*/React.createElement(Button, {
      disabled: !!busy,
      style: {
        color: "var(--plk-danger-text)"
      },
      onClick: () => this.act("cancel", {}, "Canceled.")
    }, "Yes, cancel")) : /*#__PURE__*/React.createElement(Button, {
      disabled: !!busy,
      onClick: () => this.setState({
        confirmCancel: true
      })
    }, "Cancel") : null, /*#__PURE__*/React.createElement(Button, {
      disabled: !!busy,
      onClick: this.duplicate
    }, busy === "duplicate" ? "…" : "Duplicate"), st === "Archived" ? /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !!busy,
      onClick: () => this.setStatus(restoreTo, "Restored.")
    }, busy === "status" ? "…" : `Restore to ${statusLabel(restoreTo)}`) : null, canArchive(c) ? /*#__PURE__*/React.createElement(Button, {
      disabled: !!busy,
      title: "Hide it from the campaign list; stats, recipients and events are kept",
      onClick: () => this.setStatus("Archived", "Archived.")
    }, busy === "status" ? "…" : "Archive") : null, canDelete(c) ? confirmDelete ? this.renderDeleteConfirm(c) : /*#__PURE__*/React.createElement(Button, {
      disabled: !!busy,
      onClick: () => this.setState({
        confirmDelete: permanentDelete(c) ? "named" : "simple",
        typed: "",
        confirmCancel: false
      })
    }, "Delete") : null, st === "Sent" && c.kind !== "resend" ? /*#__PURE__*/React.createElement(Button, {
      disabled: !!busy,
      onClick: () => this.setState({
        resend: resend ? null : {
          afterDays: settings.resend && settings.resend.afterDays || 3,
          subject: settings.resend && settings.resend.subject || `In case you missed it: ${(d.content || {}).subject || c.name}`
        }
      })
    }, "Resend to non-openers") : null, c.externalUrl ? /*#__PURE__*/React.createElement("a", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      href: c.externalUrl,
      target: "_blank",
      rel: "noopener noreferrer"
    }, "Open in ", railLabel(c.rail)) : null);
    const guardLine = sent > 0 ? `guard: complaints ${pct(complaintRate, 2)} · hard bounces ${pct(hardRate, 1)} (${GUARD.autoPause ? "pauses" : "warns"} at ${GUARD.complaintPct.toFixed(2)} % / ${GUARD.hardBouncePct} %)` : `guard pauses at ${GUARD.complaintPct.toFixed(2)} % complaints / ${GUARD.hardBouncePct} % hard bounces`;
    const gstate = sent > 0 ? guardState(complaintRate, hardRate) : "ok";
    const guardHot = gstate === "hot";
    const guardWarm = gstate === "warm";
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view ec-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        to: "/Email/campaigns"
      }, "Email"), " \xB7 ", /*#__PURE__*/React.createElement(Link, {
        to: "/Email/campaigns"
      }, "Campaigns"), " \xB7 Campaign"),
      title: /*#__PURE__*/React.createElement(React.Fragment, null, c.name, " ", /*#__PURE__*/React.createElement(Pill, {
        tone: statusTone(st)
      }, statusLabel(st)), c.kind === "ab_test" ? /*#__PURE__*/React.createElement(Pill, {
        tone: "info"
      }, "A/B") : null),
      subtitle: subtitleBits.join(" · "),
      actions: actions
    }), actionErr ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger"
    }, actionErr) : null, st === "Rejected" && c.rejectReason ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger"
    }, /*#__PURE__*/React.createElement("b", null, "Rejected", c.reviewedBy ? ` by ${c.reviewedBy}` : "", ":"), " ", c.rejectReason) : null, st === "Failed" && c.lastError ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger"
    }, /*#__PURE__*/React.createElement("b", null, "Failed:"), " ", c.lastError) : null, st === "Archived" ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, /*#__PURE__*/React.createElement("b", null, "Archived"), d.archived && d.archived.date ? ` ${dateTime(d.archived.date)}` : "", ". Hidden from the campaign list; stats, recipients, links and the timeline are kept. Restore returns it to ", statusLabel(restoreTo), c.kind === "recurring" && !c.parentId ? " and resumes the recurrence" : "", ".") : null, localSend && (st === "Sending" || st === "Scheduled" || st === "Paused") ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, /*#__PURE__*/React.createElement("b", null, "Local-hour send:"), " ", num(localSent), " of ", num(localTotal), " sent", delivery.windowEnd ? ` · window ends ${dateTime(delivery.windowEnd, sch.timezone)}` : "", delivery.windowStart && !delivery.windowEnd ? ` · window started ${dateTime(delivery.windowStart, sch.timezone)}` : "", ". Each person gets it at ", delivery.localHour == null ? sch.localHour == null ? 10 : sch.localHour : delivery.localHour, ":00 their time; people without a known location get ", sch.timezone || "the campaign's timezone", ".") : null, resendPlannedAt && st === "Sent" ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, /*#__PURE__*/React.createElement("b", null, "Resend to non-openers"), " planned for ", dateTime(resendPlannedAt, sch.timezone), settings.resend && settings.resend.subject ? /*#__PURE__*/React.createElement(React.Fragment, null, " with subject \u201C", settings.resend.subject, "\u201D") : null, ". A separate resend campaign is created as a Draft for the people who did not open.") : settings.resend && settings.resend.afterDays && (st === "Scheduled" || st === "Sending" || st === "Paused") ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, /*#__PURE__*/React.createElement("b", null, "Resend to non-openers"), " ", settings.resend.afterDays, " day", Number(settings.resend.afterDays) === 1 ? "" : "s", " after send", settings.resend.subject ? /*#__PURE__*/React.createElement(React.Fragment, null, " with subject \u201C", settings.resend.subject, "\u201D") : null, ".") : null, st === "InReview" ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice warning"
    }, /*#__PURE__*/React.createElement("b", null, "In review."), " Submitted", c.submittedBy ? ` by ${c.submittedBy}` : "", " ", dateTime(c.submittedDate), "; waiting for an Administrator in ", /*#__PURE__*/React.createElement(Link, {
      to: "/CampaignReview"
    }, "Campaign Review"), ".") : null, resend ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, /*#__PURE__*/React.createElement("b", null, "Resend to non-openers"), /*#__PURE__*/React.createElement("div", {
      className: "ec-inline-form",
      style: {
        marginTop: 6
      }
    }, "after ", /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "1",
      max: "14",
      style: {
        ...INPUT,
        width: 60,
        display: "inline-block"
      },
      value: resend.afterDays,
      onChange: e => this.setState({
        resend: {
          ...resend,
          afterDays: Math.max(1, Math.min(14, Number(e.target.value) || 3))
        }
      })
    }), " days with subject", /*#__PURE__*/React.createElement("input", {
      style: {
        ...INPUT,
        flex: "1 1 280px"
      },
      value: resend.subject,
      onChange: e => this.setState({
        resend: {
          ...resend,
          subject: e.target.value
        }
      })
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !!busy,
      onClick: this.createResend
    }, "Create resend draft"), /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        resend: null
      })
    }, "Cancel"))) : null, st === "Sending" || st === "Paused" || st === "Sent" && total ? /*#__PURE__*/React.createElement(React.Fragment, null, guardWarm ? /*#__PURE__*/React.createElement("div", {
      className: "ec-note",
      style: {
        marginBottom: 8
      }
    }, /*#__PURE__*/React.createElement("b", null, "Close to the deliverability limit."), " Complaints ", pct(complaintRate, 2), " and hard bounces ", pct(hardRate, 1), " are at or past ", GUARD.warnAtPct, " % of the ", GUARD.complaintPct.toFixed(2), " % / ", GUARD.hardBouncePct, " % limits", GUARD.autoPause ? "; the guard pauses the campaign if a limit is reached." : "; auto-pause is off, so it keeps sending unless you pause it.", " Check the list's consent and acquisition source.") : null, guardHot && !GUARD.autoPause && st === "Sending" ? /*#__PURE__*/React.createElement("div", {
      className: "ec-note ec-err",
      style: {
        marginBottom: 8
      }
    }, /*#__PURE__*/React.createElement("b", null, "A deliverability limit is reached and auto-pause is off."), " Pause the campaign or raise the limit under Settings.") : null, /*#__PURE__*/React.createElement("div", {
      className: "ec-card ec-progress" + (guardHot ? " hot" : guardWarm ? " warm" : "")
    }, /*#__PURE__*/React.createElement("div", {
      className: "row"
    }, /*#__PURE__*/React.createElement("b", null, st === "Sent" ? "Delivery" : "Progress"), /*#__PURE__*/React.createElement("span", {
      className: "mini"
    }, total ? `${num(progressed)} / ${num(total)} processed · ${num(counts.sent)} sent · ${num(counts.delivered)} delivered · ${num(counts.failed)} failed · ${num(counts.queued)} queued` : "waiting for the first batch", sch.throttlePerHour ? ` · throttle ${num(sch.throttlePerHour)}/h` : "", c.startedDate ? ` · started ${dateTime(c.startedDate)}` : ""), /*#__PURE__*/React.createElement("span", {
      className: "right mini" + (guardHot ? " ec-err" : "")
    }, guardLine)), /*#__PURE__*/React.createElement("div", {
      className: "ec-prog"
    }, /*#__PURE__*/React.createElement("i", {
      style: {
        width: `${progressPct}%`
      }
    })), st === "Sending" ? /*#__PURE__*/React.createElement("div", {
      className: "mini",
      style: {
        marginTop: 4
      }
    }, "Live: refreshes every 15 s.") : null)) : null, /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      tiles: tiles,
      cols: 6
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, this.renderCompare(), /*#__PURE__*/React.createElement(Card, {
      span: 8,
      title: resolved ? "Resolved at send time" : "Estimated audience",
      hint: resolved ? `the dispatcher's truth${resolved.computedDate ? ` · ${dateTime(resolved.computedDate)}` : ""}` : "the wizard estimate; recomputed at send time"
    }, /*#__PURE__*/React.createElement(Funnel, {
      f: resolved || d.estimate,
      compact: !!resolved,
      emptyText: "No audience resolved yet."
    }), /*#__PURE__*/React.createElement("div", {
      className: "mini",
      style: {
        marginTop: 8
      }
    }, (audience.include || []).length, " include list", (audience.include || []).length === 1 ? "" : "s", (audience.exclude || []).length ? ` · ${(audience.exclude || []).length} excluded` : "", audience.excludeInactive === false ? "" : " · Inactive excluded", audience.excludeEmailedDays ? ` · emailed in last ${audience.excludeEmailedDays} d excluded` : ""), c.kind === "ab_test" ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "h4"
    }, "Variants"), !variants.length ? /*#__PURE__*/React.createElement(Empty, null, "Variant stats arrive once the test sample has gone out.") : /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Key"), /*#__PURE__*/React.createElement("th", null, "Subject"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Sent"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Clicks"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Rate"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Opens"))), /*#__PURE__*/React.createElement("tbody", null, variants.map(v => {
      const spec = (d.variants && d.variants.variants || []).find(x => x.key === v.variantKey) || {};
      return /*#__PURE__*/React.createElement("tr", {
        key: v.variantKey || "?"
      }, /*#__PURE__*/React.createElement("td", null, v.variantKey || "remainder", " ", winner && winner === v.variantKey ? /*#__PURE__*/React.createElement(Pill, {
        tone: "success"
      }, "winner") : null), /*#__PURE__*/React.createElement("td", null, spec.subject || (d.content || {}).subject || "—"), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(v.sent)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(v.uniqueClicks)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, pct(v.clickRate)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, pct(v.openRate)));
    })))), d.variants ? /*#__PURE__*/React.createElement("div", {
      className: "mini"
    }, d.variants.sample || 20, " % sample \xB7 winner by ", d.variants.winnerMetric || "clicks", " after ", Math.round((d.variants.waitMinutes || 240) / 60), " h", d.variants.autoWinner === false ? " · held for a human" : " · auto-send", d.variants.winnerDecidedDate ? ` · decided ${dateTime(d.variants.winnerDecidedDate)}` : "") : null) : null, /*#__PURE__*/React.createElement("div", {
      className: "h4"
    }, "Links"), !links ? /*#__PURE__*/React.createElement("div", {
      className: "territory-hint"
    }, "Loading\u2026") : !links.length ? /*#__PURE__*/React.createElement(Empty, null, "No link clicks yet.") : /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "URL"), c.kind === "ab_test" ? /*#__PURE__*/React.createElement("th", null, "Variant") : null, /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Clicks"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Unique"))), /*#__PURE__*/React.createElement("tbody", null, links.map(l => /*#__PURE__*/React.createElement("tr", {
      key: idStr(l.id)
    }, /*#__PURE__*/React.createElement("td", {
      className: "ec-url",
      title: l.url
    }, String(l.url || "").replace(/^https?:\/\/[^/]+/, "") || l.url), c.kind === "ab_test" ? /*#__PURE__*/React.createElement("td", null, l.variantKey || "—") : null, /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(l.clicks)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(l.uniqueClicks))))))), /*#__PURE__*/React.createElement("div", {
      className: "h4"
    }, "Recipients"), /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar"
    }, /*#__PURE__*/React.createElement(Filters, null, /*#__PURE__*/React.createElement(Search, {
      value: this.state.recQ,
      onChange: v => this.setState({
        recQ: v
      }, () => {
        clearTimeout(this._qT);
        this._qT = setTimeout(() => this.loadRecipients(0), 400);
      }),
      placeholder: "Search email"
    }), /*#__PURE__*/React.createElement(PlunkDropDown, {
      options: [["all", "Any status"]].concat(RECIPIENT_STATUSES.map(x => [x, x])),
      value: this.state.recStatus,
      onChange: this.recFilter("recStatus"),
      width: "150px"
    })), /*#__PURE__*/React.createElement("span", {
      className: "mini"
    }, num(recTotal), " match", total ? ` · ${num(counts.bounced)} bounced · ${num(counts.complained)} complained · ${num(counts.unsubscribed)} unsubscribed` : "")), !recipients ? /*#__PURE__*/React.createElement("div", {
      className: "territory-hint"
    }, "Loading\u2026") : !recipients.length ? /*#__PURE__*/React.createElement(Empty, null, total ? "No recipient matches." : "Recipients are resolved when the campaign starts sending.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Email"), /*#__PURE__*/React.createElement("th", null, "Status"), c.kind === "ab_test" ? /*#__PURE__*/React.createElement("th", null, "Variant") : null, /*#__PURE__*/React.createElement("th", null, "Sent"), /*#__PURE__*/React.createElement("th", null, "Opened"), /*#__PURE__*/React.createElement("th", null, "Clicked"), /*#__PURE__*/React.createElement("th", null, "Error"))), /*#__PURE__*/React.createElement("tbody", null, recipients.map(r => /*#__PURE__*/React.createElement("tr", {
      key: r.id
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, r.email), r.personId ? /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, "person ", r.personId) : null), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Pill, {
      tone: recipientTone(r.status)
    }, r.status), r.bounceType ? /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, r.bounceType) : null), c.kind === "ab_test" ? /*#__PURE__*/React.createElement("td", null, r.variantKey || "—") : null, /*#__PURE__*/React.createElement("td", {
      className: "dim"
    }, dateTime(r.sentDate)), /*#__PURE__*/React.createElement("td", {
      className: "dim"
    }, r.openedDate ? `${dateTime(r.openedDate)}${r.opens > 1 ? ` ×${r.opens}` : ""}` : "—"), /*#__PURE__*/React.createElement("td", {
      className: "dim"
    }, r.clickedDate ? `${dateTime(r.clickedDate)}${r.clicks > 1 ? ` ×${r.clicks}` : ""}` : "—"), /*#__PURE__*/React.createElement("td", {
      className: "dim",
      title: r.error || ""
    }, r.error ? String(r.error).slice(0, 60) : "—")))))), recipients.length < recTotal ? /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar",
      style: {
        marginTop: 10
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "wa-chip",
      onClick: () => this.loadRecipients(this.state.recSkip + PAGE)
    }, "Show more \xB7 ", num(recTotal - recipients.length), " more")) : null)), /*#__PURE__*/React.createElement(Card, {
      span: 4,
      title: "Timeline",
      hint: "approval, schedule, guard events"
    }, !timeline ? /*#__PURE__*/React.createElement("div", {
      className: "territory-hint"
    }, "Loading\u2026") : !timeline.length ? /*#__PURE__*/React.createElement(Empty, null, "Nothing logged yet.") : /*#__PURE__*/React.createElement("ul", {
      className: "ec-timeline"
    }, timeline.map(t => {
      const det = t.details && typeof t.details === "object" ? t.details : null;
      const bits = det ? [det.reason, det.to && Array.isArray(det.to) ? `to ${det.to.join(", ")}` : null, det.before && det.after && det.before !== det.after ? `${det.before} → ${det.after}` : null, det.target && !det.after ? `→ ${det.target}` : null].filter(Boolean) : [];
      return /*#__PURE__*/React.createElement("li", {
        key: idStr(t.id),
        className: String(t.severity || "").toLowerCase()
      }, /*#__PURE__*/React.createElement("div", {
        className: "when"
      }, dateTime(t.createdDate)), /*#__PURE__*/React.createElement("div", {
        className: "what"
      }, /*#__PURE__*/React.createElement("b", null, String(t.action || "").replace(/^email_campaign\./, "").replace(/_/g, " ")), t.actorName || t.actorId ? /*#__PURE__*/React.createElement("span", {
        className: "dim"
      }, " \xB7 ", t.actorName || t.actorId) : null, t.outcome && t.outcome !== "Success" ? /*#__PURE__*/React.createElement(Pill, {
        tone: "danger"
      }, t.outcome) : null), bits.length ? /*#__PURE__*/React.createElement("div", {
        className: "code"
      }, bits.join(" · ")) : null);
    })), /*#__PURE__*/React.createElement("div", {
      className: "h4"
    }, "Content"), /*#__PURE__*/React.createElement("div", {
      className: "mini"
    }, "Subject: ", /*#__PURE__*/React.createElement("b", null, (d.content || {}).subject || "—"), (d.content || {}).preheader ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", (d.content || {}).preheader) : null, /*#__PURE__*/React.createElement("br", null), "source: ", (d.content || {}).source || "composer", settings.testSent ? ` · test sent ${dateTime(settings.testSentDate)}` : " · no test sent", settings.lintClean === true ? " · checks clean" : settings.lintClean === false ? " · checks found errors" : ""), d.sender ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "h4"
    }, "Sender"), /*#__PURE__*/React.createElement("div", {
      className: "mini"
    }, d.sender.fromName, " <", d.sender.fromEmail, "> \xB7 ", d.sender.domain, " \xB7 ", d.senderVerified ? /*#__PURE__*/React.createElement(Pill, {
      tone: "success"
    }, "verified") : /*#__PURE__*/React.createElement(Pill, {
      tone: "warning"
    }, d.sender.domainAuthStatus || "unverified"))) : null, settings.rail && (settings.rail.providerStatus || settings.rail.membersPushed != null) ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "h4"
    }, railLabel(c.rail)), /*#__PURE__*/React.createElement("div", {
      className: "mini"
    }, settings.rail.providerStatus ? `provider status ${settings.rail.providerStatus}` : "", settings.rail.membersPushed != null ? ` · ${num(settings.rail.membersPushed)} members pushed${settings.rail.membersFailed ? ` (${num(settings.rail.membersFailed)} failed)` : ""}` : "", settings.rail.lastStatsPull ? ` · stats ${dateTime(settings.rail.lastStatsPull)}` : "", settings.rail.lastPullError ? /*#__PURE__*/React.createElement("span", {
      className: "ec-err"
    }, " \xB7 ", settings.rail.lastPullError) : null)) : null)));
  }
}
export default withRouter(EmailCampaignDetailViewComponent);